/**
 * MKS-493 — the Theme section of `/profile`.
 *
 * A segmented control over the two themes. Picking one applies it to the
 * whole SPA immediately (ThemeProvider rewrites `data-theme` on `<html>`)
 * and persists it to the preferences JSONB plus `localStorage['ms-theme']`.
 */
import { Card } from '../ui/Card';
import { useTheme, THEMES, type Theme } from '../../services/themeContext';

const LABELS: Record<Theme, string> = {
  default: 'Default',
  sparkle: 'Sparkle',
};

export function ThemeField() {
  const { theme, setTheme } = useTheme();

  return (
    <Card title="Theme">
      <div
        role="radiogroup"
        aria-label="Theme"
        className="ms-segmented inline-flex rounded-ms-pill border border-ms-border bg-ms-surface-2 p-0.5"
      >
        {THEMES.map((option) => {
          const selected = theme === option;
          return (
            <button
              key={option}
              type="button"
              role="radio"
              aria-checked={selected}
              onClick={() => setTheme(option)}
              className={`rounded-ms-pill px-3 py-1.5 text-[12px] font-medium transition-colors ${
                selected
                  ? 'ms-segment-active bg-ms-orange text-[color:var(--ms-on-accent)]'
                  : 'text-ms-text-muted hover:text-ms-text-strong'
              }`}
            >
              {LABELS[option]}
              {/* The "✨ Sparkle" item in the switcher. The emoji is part of
                  the Sparkle option's own label, so unlike everywhere else it
                  has to show while the DEFAULT theme is active too — that is
                  how you see what you are about to pick. Hence the literal
                  here rather than <ThemeEmoji>, which renders nothing off
                  Sparkle; the aria-hidden span keeps it out of the radio's
                  accessible name either way. */}
              {option === 'sparkle' && (
                <span aria-hidden="true"> ✨</span>
              )}
            </button>
          );
        })}
      </div>
      <p className="text-[10.5px] text-ms-text-muted mt-3">
        Sparkle is a playful pink look for the whole app. Your choice is saved
        to your account and applies on every device you sign in on.
      </p>
    </Card>
  );
}
